import React, { useMemo, useState } from "react";
import { useAppSelector } from "app/hooks";
import { Link as NavLink } from "react-router-dom";
import { alpha, Avatar, Box, colors, IconButton, Link, Tooltip, Typography } from "@mui/material";
import { selectUserIsAdmin } from "features/auth/authSlice";
import EditIcon from "@mui/icons-material/EditOutlined";
import EditCustomerData from "../dialogs/EditCustomerData";
import { useOrderContext } from "context/OrderContext";

const translateOrderStatus = (status: string | null | undefined) => {
  switch (status) {
    case "PENDING":
      return {
        code: "2",
        text: "Pågående",
        color: colors.orange,
      } as const;
    case "COMPLETE":
      return {
        code: "4",
        text: "Slutförd",
        color: colors.green,
      } as const;
    case "INCOMPLETE":
      return {
        code: "X",
        text: "Ej slutförd",
        color: colors.red,
      } as const;
    default:
      return {
        code: "?",
        text: "Okänd",
        color: colors.grey,
      } as const;
  }
};

const OrderSummary: React.FC = () => {
  const order = useOrderContext();
  const isAdmin = useAppSelector(selectUserIsAdmin);
  const [showEditModal, setShowEditModal] = useState(false);

  const status = useMemo(() => {
    return translateOrderStatus(order.completion?.completionState);
  }, [order.completion?.completionState]);

  return (
    <Box
      sx={(theme) => ({
        p: 2,
        backgroundImage: `linear-gradient(to right, ${status.color[50]}, ${status.color[100]})`,
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 1,
        position: "relative",
        pl: 3,
        "::before": {
          content: "''",
          position: "absolute",
          backgroundColor: alpha(status.color[700], 0.6),
          left: 0,
          insetBlock: "0",
          width: "0.5rem",
        },
        [theme.breakpoints.down("sm")]: {
          gridTemplateColumns: "unset",
        },
      })}
    >
      <Box>
        <Typography component="span" sx={{ fontSize: "1.5rem", fontWeight: "bold" }}>
          {order.ordernr}
        </Typography>

        <Typography fontSize="2rem" fontWeight="bold">
          {order.companyName}

          {isAdmin && (
            <Tooltip arrow title={"Redigera kunduppgifter"}>
              <IconButton onClick={() => setShowEditModal(true)}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
        </Typography>

        <Typography>{order.address}</Typography>
        <Typography>
          {order.postnr} {order.postaddress}
        </Typography>
      </Box>

      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          textAlign: "right",
          justifyItems: "end",
          alignItems: "end",
          gap: 1,
          [theme.breakpoints.down("sm")]: {
            flexDirection: "row-reverse",
          },
        })}
      >
        <Avatar
          variant="rounded"
          sx={{
            bgcolor: status.color[700],
            color: "white",
            width: 64,
            height: 64,
            fontSize: "3rem",
            fontWeight: "bold",
          }}
        >
          {status.code}
        </Avatar>

        <Box
          sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            [theme.breakpoints.down("sm")]: {
              justifyContent: "center",
            },
          })}
        >
          <Typography sx={{ fontWeight: "bold" }}>{status.text}</Typography>

          <Typography sx={{ fontSize: "small" }}>
            {order?.completion?.completedAt?.slice(0, 16)}
          </Typography>

          <Tooltip arrow title="Gå till användarprofilen" sx={{}}>
            <Link
              component={NavLink}
              to={`/users/view/${order?.completion?.completedBy?.id}`}
              fontSize="small"
            >
              {order?.completion?.completedBy?.userName}
            </Link>
          </Tooltip>
        </Box>
      </Box>

      {order.completion?.completionState === "INCOMPLETE" && order.completion?.installerComment && (
        <Box sx={{ gridColumn: "1 / -1", textAlign: "right" }}>
          <Typography fontWeight="bold">Kommentar</Typography>
          <Typography>{order.completion?.installerComment}</Typography>
        </Box>
      )}

      <EditCustomerData show={showEditModal} setShow={setShowEditModal} />
    </Box>
  );
};

export default OrderSummary;
